import React, { useMemo, useState } from 'react'
import { createHtmlPortalNode, OutPortal } from 'react-reverse-portal'
import { DateRange } from '../../../../lib/types/DateRange'
import dayjs from 'dayjs'
import { DateRangePicker, Info, Pill, Table } from '@demandlocal/components'
import { formatNumber } from '../../../../lib/utils/formatNumber'
import { Outlet } from 'react-router'
import { motion } from 'framer-motion'
import { createColumnHelper } from '@tanstack/react-table'
import { useGetTrackingTagMetrics } from '../../../../api/dealership/customers/sources/useGetTrackingTagMetrics'
import MultiLineChart from '../../../../components/Charts/MultiLineChart'
import { useGetTrackingTags } from '../../../../api/dealership/customers/sources/useGetTrackingTags'
import { ReactComponent as LogoVerticalSimple } from '../../../../images/logo_simple_colored.svg'
import EmptyTagAudiences from './EmptyTagAudiences'

const trackingTagColumnHelper = createColumnHelper<{
  vendor: string
  segment_name: string
  tag_name: string
  segment_id: string
  one_day_hits: number
  seven_day_hits: number
  thirty_day_hits: number
}>()

export default function SiteAudiences(): JSX.Element {
  const filtersPortalNode = useMemo(() => createHtmlPortalNode(), [])
  const [filteredTotal, setFilteredTotal] = useState<number>(0)
  const [filters, setFilters] = useState<DateRange>({
    dateRange: {
      startDate: dayjs().subtract(7, 'day').format('YYYY-MM-DD'),
      endDate: dayjs().format('YYYY-MM-DD'),
    },
  })

  const columns = useMemo(() => {
    return [
      trackingTagColumnHelper.accessor('vendor', {
        header: 'Source',
        cell: (info) =>
          info.getValue() === 'TTD' ? (
            <LogoVerticalSimple className=" pl-2 h-[25px] w-[25px]" />
          ) : (
            <Pill type={'rainbow'}>{info.getValue()}</Pill>
          ),
      }),
      trackingTagColumnHelper.accessor('segment_name', {
        header: () => {
          return (
            <div className="flex flex-row gap-2 items-center">
              <span>Segments</span>
              <Info message="A collection of users based on our site pixel and users actions. Creating a segment that can be used like an audience" />
            </div>
          )
        },
        cell: (info) => info.getValue(),
      }),
      trackingTagColumnHelper.accessor('tag_name', {
        header: 'Type',
        cell: (info) => info.getValue(),
      }),
      trackingTagColumnHelper.accessor('one_day_hits', {
        header: '1 Day Page Views',
        cell: (info) => formatNumber(info.getValue() || 0),
      }),
      trackingTagColumnHelper.accessor('seven_day_hits', {
        header: '7 Day Page Views',
        cell: (info) => formatNumber(info.getValue() || 0),
      }),
      trackingTagColumnHelper.accessor('thirty_day_hits', {
        header: '30 Day Page Views',
        cell: (info) => formatNumber(info.getValue() || 0),
      }),
    ]
  }, [])
  const { data, isLoading, isPreviousData } = useGetTrackingTagMetrics({
    filters: filters.dateRange,
  })

  const onDateRangeChange = (dateRange: DateRange): void => {
    setFilters((prev: DateRange) => ({
      ...prev,
      ...dateRange,
    }))
  }

  const { data: thirdPartyAudiences, isLoading: isLoadingThirdPartyAudiences } =
    useGetTrackingTags({ pageSize: 1 })

  const hasAudiences = useMemo(
    () =>
      !isLoadingThirdPartyAudiences &&
      thirdPartyAudiences?.pagination?.totalDocs &&
      thirdPartyAudiences?.pagination?.totalDocs > 0,
    [isLoadingThirdPartyAudiences, thirdPartyAudiences?.pagination.totalDocs],
  )

  return (
    <motion.div className={'flex flex-1 flex-col gap-8 p-10 font-Spartan'}>
      {hasAudiences ? (
        <div className={'w-full flex flex-1 flex-col gap-2'}>
          <span className="text-md py-2">
            Audiences built from your site traffic and vendor tags for programmatic use.
            These users' PII is not known, but you can still target them with advertising.
          </span>
          <div className={'min-h-[270px] border-2 rounded-xl'}>
            <div
              className={
                'pt-5 pb-2 px-5 flex font-bold text-sm text-primary-xiketic justify-between items-center'
              }>
              <div className="flex flex-row gap-2 items-center">
                <span>Daily Page Views</span>
                <Info
                  message={
                    'Daily page views counts all views on that particular day, even if one user is seen multiple times.'
                  }
                />
              </div>
              <DateRangePicker filters={filters} onChange={onDateRangeChange} />
            </div>
            <div className={'h-[270px] p-2'}>
              <MultiLineChart
                data={
                  data?.map((d) => {
                    return {
                      date: d.date,
                      tag: d.segment_name,
                      hits: d.hits,
                    }
                  }) ?? []
                }
                customOptions={{
                  elements: {
                    line: {
                      tension: 0,
                    },
                    point: {
                      radius: 0,
                    },
                  },
                  plugins: {
                    legend: {
                      position: 'bottom',
                    },
                    tooltip: {
                      mode: 'index' as const,
                      intersect: false,
                    },
                  },
                }}
                gradient={[
                  'blue-to-purple',
                  'red-to-orange',
                  'green-to-cyan',
                  'green-to-lemon-green',
                  'blue-to-cyan',
                  'blue-to-purple',
                  'red-to-orange',
                  'green-to-cyan',
                  'green-to-lemon-green',
                  'blue-to-cyan',
                ]}
                borderColor="white"
                groupKey={'tag'}
                labelKey={'tag'}
                yKey={'hits'}
                xKey={'date'}
                isLoading={isLoading}
                isPreviousData={isPreviousData}
              />
            </div>
          </div>
          <div className={'w-full flex flex-1 flex-col gap-2'}>
            <div className={'pb-4 flex justify-between'}>
              <div>
                <div
                  className={'font-Spartan font-bold text-white p-2 rounded-md'}
                  style={{ fontSize: 10, backgroundColor: '#121124' }}>
                  {formatNumber(filteredTotal)} segments
                </div>
              </div>
              <div>
                <OutPortal node={filtersPortalNode} />
              </div>
            </div>
            <Table
              columns={columns}
              fetchFn={useGetTrackingTags}
              filters={{}}
              defaultSorting={[{ id: 'vendor', desc: true }]}
              setTotalRows={(v) => setFilteredTotal(v)}
            />
          </div>
          <Outlet />
        </div>
      ) : (
        <EmptyTagAudiences />
      )}
    </motion.div>
  )
}
